import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FolderTree, Plus, Pencil, Trash2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import DataTable from "@/components/app/DataTable";
import Modal from "@/components/app/Modal";
import { Field, useValidatedForm, isBlank, toNum } from "@/components/app/FormKit";
import { btnOutline, btnPrimary, iconBtn, pageSub, pageTitle } from "@/components/app/ui";

const empty = { name: "", parent: "", description: "", sort_order: "0", status: "active" };
const STATUS_OPTS = [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }];

export default function Categories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/vendor/categories").then(({ data }) => setItems(data)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const validate = useCallback((f) => {
    const e = {};
    const name = f.name.trim();
    if (!name) e.name = "Category name is required.";
    else if (name.length < 2) e.name = "Name must be at least 2 characters.";
    else if (name.length > 60) e.name = "Name must be 60 characters or fewer.";
    else if (items.some((c) => c.id !== editId && (c.parent || "") === (f.parent || "") && c.name.trim().toLowerCase() === name.toLowerCase()))
      e.name = "A category with this name already exists at this level.";
    if (f.parent && f.parent === editId) e.parent = "A category cannot be its own parent.";
    if (!isBlank(f.sort_order) && (!Number.isInteger(toNum(f.sort_order)) || toNum(f.sort_order) < 0)) e.sort_order = "Whole number, 0 or more.";
    return e;
  }, [items, editId]);

  const form = useValidatedForm(empty, validate);
  const { bind } = form;

  const openNew = () => { form.reset(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => {
    form.reset({ ...empty, ...c, parent: c.parent || "", description: c.description || "", sort_order: String(c.sort_order ?? 0), status: c.status || "active" });
    setEditId(c.id); setOpen(true);
  };

  const save = async () => {
    if (!form.check()) return;
    setSaving(true);
    const v = form.values;
    const payload = { ...v, name: v.name.trim(), parent: v.parent || null, sort_order: parseInt(v.sort_order, 10) || 0 };
    try {
      if (editId) await api.put(`/vendor/categories/${editId}`, payload);
      else await api.post("/vendor/categories", payload);
      toast.success(`Category “${payload.name}” ${editId ? "updated" : "created"} successfully.`);
      setOpen(false); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete category “${c.name}”?`)) return;
    try { await api.delete(`/vendor/categories/${c.id}`); toast.success(`Category “${c.name}” deleted.`); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const nameOf = useCallback((id) => items.find((c) => c.id === id)?.name || "", [items]);

  const columns = useMemo(() => [
    { key: "name", header: "Category", cell: (c) => <span className="font-semibold text-neutral-900">{c.name}</span> },
    { key: "slug", header: "Slug", hideBelow: "md", cell: (c) => <span className="font-mono text-[12px] text-slate-500">/{c.slug}</span> },
    { key: "parent", header: "Parent", accessor: (c) => nameOf(c.parent), cell: (c) => <span className="text-slate-600">{nameOf(c.parent) || "— Top level"}</span> },
    { key: "sort_order", header: "Order", align: "center", hideBelow: "sm", accessor: (c) => c.sort_order ?? 0 },
    { key: "status", header: "Status", accessor: (c) => c.status || "active", cell: (c) => (
      <span className={`inline-block text-[11px] px-2 py-0.5 rounded-md border font-medium capitalize ${(c.status || "active") === "active" ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{c.status || "active"}</span>
    ) },
    { key: "actions", header: "", sortable: false, align: "right", cell: (c) => (
      <div className="flex justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => openEdit(c)} data-testid={`category-edit-${c.id}`} className={`${iconBtn} hover:bg-blue-50 hover:text-blue-600`} aria-label={`Edit ${c.name}`}><Pencil size={14} /></button>
        <button onClick={() => del(c)} data-testid={`category-delete-${c.id}`} className={`${iconBtn} hover:bg-red-50 hover:text-red-600`} aria-label={`Delete ${c.name}`}><Trash2 size={14} /></button>
      </div>
    ) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [nameOf]);

  const filters = useMemo(() => [
    { key: "status", label: "All status", accessor: (c) => c.status || "active", options: STATUS_OPTS },
    { key: "level", label: "All levels", accessor: (c) => (c.parent ? "child" : "top"), options: [{ value: "top", label: "Top level" }, { value: "child", label: "Subcategories" }] },
  ], []);

  return (
    <div className="space-y-4" data-testid="vendor-categories">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className={pageTitle}>Categories</h1>
          <p className={pageSub}>{items.length} categories. Supports parent → subcategory.</p>
        </div>
        <button onClick={openNew} data-testid="category-add-btn" className={btnPrimary}><Plus size={15} /> Add category</button>
      </div>

      <DataTable testId="categories-table" exportName="categories" columns={columns} data={items} loading={loading} rowTestId={(c) => `category-${c.id}`}
        searchKeys={["name", "slug"]} searchPlaceholder="Search categories…" filters={filters} defaultSort={{ key: "sort_order", dir: "asc" }} onRowClick={openEdit}
        emptyIcon={FolderTree} emptyTitle="No categories yet" emptyText="Create categories to organise your products." />

      <Modal open={open} onClose={() => setOpen(false)} size="lg" testId="category-modal" title={editId ? "Edit category" : "New category"} subtitle="Fields marked * are required."
        footer={<>
          <button onClick={() => setOpen(false)} className={btnOutline}>Cancel</button>
          <button onClick={save} disabled={saving} data-testid="category-save" className={btnPrimary}>{saving ? "Saving…" : editId ? "Save changes" : "Create category"}</button>
        </>}>
        <div className="grid grid-cols-2 gap-x-3 gap-y-3">
          <Field className="col-span-2 sm:col-span-1" label="Name" required maxLength={60} t="category-name" placeholder="e.g. Footwear" {...bind("name")} />
          <Field className="col-span-2 sm:col-span-1" label="Parent category" type="select" t="category-parent" placeholder="— None (top level) —"
            options={items.filter((c) => c.id !== editId).map((c) => ({ value: c.id, label: c.name }))} {...bind("parent")} />
          <Field className="col-span-2 sm:col-span-1" label="Sort order" type="number" min="0" step="1" t="category-sort" hint="Lower numbers appear first." {...bind("sort_order")} />
          <Field className="col-span-2 sm:col-span-1" label="Status" type="select" options={STATUS_OPTS} t="category-status" {...bind("status")} />
          <Field className="col-span-2" label="Description" type="textarea" rows={2} maxLength={200} t="category-description" {...bind("description")} />
        </div>
      </Modal>
    </div>
  );
}
